import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import TruncatedText from '../components/TruncatedText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';

function WishListSoloPage() {
  const { t } = useTranslation();
  const { shareableLink } = useParams();
  const [wishList, setWishList] = useState(null);
  const [items, setItems] = useState([]);
  const [itemStatuses, setItemStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const fetchWishList = async () => {
      try {
        const response = await axios.get(`/api/wish-lists/shared/${shareableLink}`);
        setWishList(response.data.wishList);
        setItems(response.data.items);
        // initialize item statuses (if server provides status use it, otherwise default to 'available')
        const initialStatuses = {};
        (response.data.items || []).forEach((it) => {
          initialStatuses[it._id] = it.status || 'available';
        });
        setItemStatuses(initialStatuses);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching shared wish list:', error);
        setLoading(false);
      }
    };

    fetchWishList();
  }, [shareableLink]);

  if (loading) {
    return <div>{t('loading')}...</div>;
  }

  if (!wishList) {
    return <div>{t('wishListNotFound')}</div>;
  }

  return (
  <div className="w-full max-w-3xl md:max-w-3xl mx-auto p-4 bg-white dark:bg-dark-surface shadow-md rounded-lg min-h-screen transition-colors">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{wishList.name}</h1>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="Toggle theme"
        >
          <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} className="text-gray-700 dark:text-gray-200" />
        </button>
      </div>
      <ul>
        {items.map((item) => {
          const status = itemStatuses[item._id] || 'available';
          return (
            <li key={item._id} className={`border-b dark:border-gray-700 py-3 ${status === 'reserved' ? 'opacity-60' : ''}`}>
              <div className="flex items-start w-full">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center min-w-0">
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-indigo-600 dark:text-indigo-400 hover:underline block truncate mr-2">
                        {item.name}
                      </a>
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm text-gray-400" />
                    </div>

                    <div className="ml-3 flex-shrink-0">
                      <select
                        aria-label="status"
                        value={status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          setItemStatuses((s) => ({ ...s, [item._id]: newStatus }));
                          try {
                            await axios.patch(`/api/wish-lists/shared/${shareableLink}/items/${item._id}/status`, { status: newStatus });
                          } catch (err) {
                            console.error('Failed to save booking status:', err);
                            setItemStatuses((s) => ({ ...s, [item._id]: status }));
                          }
                        }}
                        className="border border-gray-300 dark:border-gray-600 dark:bg-dark-bg dark:text-white rounded-md text-sm p-1"
                      >
                        <option value="available">{t('statusAvailable')}</option>
                        <option value="reserved">{t('statusReserved')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-2 flex items-start">
                    {item.imageUrl && (
                      <div className="w-16 h-16 rounded-md overflow-hidden mr-3 flex-shrink-0">
                        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {item.description && <TruncatedText text={item.description} maxLength={100} className="text-gray-600 dark:text-gray-400" />}
                    </div>

                    {/* status dropdown moved to title row */}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default WishListSoloPage;
